import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Grid } from '@mui/material';
import { PageContainer, FilterPod, Icons } from 'components';
import axios from 'axios';
import { enifyDateTime, constBreadCrumbsTitle } from 'utility';
import { toFilterVOS, filterVOStoQueryStr } from 'components/FilterPod';
import { columns, generateData } from './components/Columns';
import InitFilters from './constants/InitFiltering';
import { useStyles } from './style';
import { TableComponents } from 'components';
import ShareSerives from 'utility/ShareServices';
import { useQuery } from 'hooks';
import FilterButton from 'components/FilterPod/FilterButton';

const AUTO_REFRESH_DELAY = 35;
const REPORT_STATUS_FIELDS = [
  'status-key',
  'status-name',
  'report-creation-date',
  'error-message',
  'downloadable',
  'download-link',
  'use-alternate-download-service',
];

export default function ReportOfflineTransactions() {
  //-- STATES --//
  const useQueryData = useQuery();
  let tableRef = useRef();
  const [loading, setLoading] = useState(false);
  const [, setFilterParams] = useState(null);
  const [size, setSize] = useState(10);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(InitFilters(useQueryData.get('source')));
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const refreshTimeoutRef = useRef(null);
  const requestInProgressRef = useRef(false);
  const isBackgroundRefreshRef = useRef(false);
  const hasPendingReportRef = useRef(false);
  const isMountedRef = useRef(true);
  const handleCloseFilterDialog = () => setOpenFilterDialog(false);
  //--HOOKS--//
  const classes = useStyles();
  const navigate = useNavigate();
  const filterEnum = {
    TRANSACTIONSWALLET: [
      'WALLET_TRANSACTION_OVER_YEAR',
      'WALLET_TRANSACTION_THREE_MONTH',
      'DIGITAL_LIVE_TRANSACTION',
      'DIGITAL_DELAY_TRANSACTION',
    ], // صورتحساب دیجیتال
    TRANSACTIONSWALLETIMPREST: [
      'WALLET_TRANSACTION_OVER_YEAR',
      'WALLET_TRANSACTION_THREE_MONTH',
      'DIGITAL_LIVE_TRANSACTION',
      'DIGITAL_DELAY_TRANSACTION',
    ], // صورتحساب دیجیتال تنخواه
    TRANSACTIONSGUILD: [
      'GUILD_TRANSACTION_OVER_YEAR',
      'GUILD_TRANSACTION_THREE_MONTH',
      'GUILD_LIVE_TRANSACTION',
      'GUILD_DELAY_TRANSACTION',
    ], // صورتحساب تلفیقی
    TRANSACTIONSGUILDWITHID: [
      'GUILD_TRANSACTION_OVER_YEAR',
      'GUILD_TRANSACTION_THREE_MONTH',
      'GUILD_LIVE_TRANSACTION',
      'GUILD_DELAY_TRANSACTION',
    ], // صورتحساب تفصیلی با شناسه حساب
    GROUPTRANSFERCREDIT: ['CONTACT'], //گزارش انتقال وجه گروهی
    FUNDERIMPRESTREPORT: ['ALL_FUNDER_IMPREST_XLS', 'ALL_FUNDER_IMPREST_PDF'], // کل تنخواه
    BRANCHTRANSACTIONS: [
      'IMPREST_TRANSACTIONS',
      'BRANCH_LIVE_TRANSACTION',
      'BRANCH_DELAY_TRANSACTION',
      'IMPREST_LIVE_TRANSACTION',
      'IMPREST_DELAY_TRANSACTION',
    ], // صورتحساب تنخواه گردان
    BRANCHTRANSACTIONSWITHID: [
      'IMPREST_TRANSACTIONS',
      'BRANCH_LIVE_TRANSACTION',
      'BRANCH_DELAY_TRANSACTION',
      'IMPREST_LIVE_TRANSACTION',
      'IMPREST_DELAY_TRANSACTION',
    ], // صورتحساب شعبه با شناسه
    WALLETANDGUILD: ['WALLET_GUILD'], // تلفیقی
    INVOICE: ['SALES_INVOICE'], // فاکتور های فروش
    INVOICECREDITOR: ['PURCHASE_INVOICE'], // فاکتور های خرید
    IMPRESTTRANSACTIONS: [
      'IMPREST_TRANSACTIONS',
      'IMPREST_LIVE_TRANSACTION',
      'IMPREST_DELAY_TRANSACTION',
    ], //  صورتحساب تنخواه گردان
    IMPRESTTRANSACTIONSWITHID: [
      'IMPREST_TRANSACTIONS',
      'IMPREST_LIVE_TRANSACTION',
      'IMPREST_DELAY_TRANSACTION',
    ], //  صورتحساب تنخواه گردان
    SETTLEMENTWALLET: ['DIGITAL_SETTLEMENT'], // گزارش درخواست های تسویه حساب دیجیتال.
    SETTLEMENTWALLETIMPREST: ['DIGITAL_SETTLEMENT'], // گزارش درخواست های تسویه حساب دیجیتال تنخواه
    SETTLEMENTGUILD: ['GUILD_SETTLEMENT'], // گزارش درخواست های تسویه حساب تفصیلی
    SETTLEMENTGUILDWITHID: ['GUILD_SETTLEMENT'], // گزارش درخواست های تسویه حساب تفصیلی با شناسه حساب
    IMPRESTLIST: ['IMPREST_LIST'], //  لیست تنخواه گردان
    REPORTSUMMARY: ['IMPREST_AGGREGATE'], // گزارش تجمیعی
    DELEGATIONLIST: ['ASSIGNED_DELEGATION'], // گزارش وجه‌های تفویض شده به سایرین
    DELEGATION: ['RECEIVED_DELEGATION'], // وجه‌های تفویض شده به من
    IMPRESTDEPOSITREPORT: ['IMPREST_DEPOSIT_TWO_MONTHS', 'IMPREST_DEPOSIT_ONE_YEAR'],
    VOUCHER: ['VOUCHER_LIST'],
  };

  const breadcrumbEnum = {
    TRANSACTIONSWALLET: constBreadCrumbsTitle.TRANSACTIONWALLET_WITHOUT_ID,
    TRANSACTIONSWALLETIMPREST: constBreadCrumbsTitle.IMPREST_TRANSACTIONWALLET_WITHOUT_ID,
    TRANSACTIONSGUILD: constBreadCrumbsTitle.TRANASACTIONS_GUILDS_LIST_WITH_ID,
    TRANSACTIONSGUILDWITHID: constBreadCrumbsTitle.TRANASACTIONS_GUILDS_LIST,
    GROUPTRANSFERCREDIT: constBreadCrumbsTitle.GROUP_TRANSFERCREDIT_LIST,
    FUNDERIMPRESTREPORT: constBreadCrumbsTitle.FUNDER_IMPREST_REPORT,
    BRANCHTRANSACTIONS: constBreadCrumbsTitle.MENU_BRANCH_TRANSACTIONS,
    BRANCHTRANSACTIONSWITHID: constBreadCrumbsTitle.BRANCH_TRANSACTIONS,
    WALLETANDGUILD: constBreadCrumbsTitle.WALLETANDGUILD,
    INVOICE: constBreadCrumbsTitle.INVOICE_LIST,
    INVOICECREDITOR: constBreadCrumbsTitle.INVOICE_CREDITOR,
    IMPRESTTRANSACTIONS: constBreadCrumbsTitle.MENU_IMPREST_TRANSACTIONS,
    IMPRESTTRANSACTIONSWITHID: constBreadCrumbsTitle.MENU_IMPREST_TRANSACTIONS_REPORT,
    SETTLEMENTWALLET: constBreadCrumbsTitle.SETTLEMENT_WALLET,
    SETTLEMENTWALLETIMPREST: constBreadCrumbsTitle.IMPREST_SETTLEMENT_WALLET,
    SETTLEMENTGUILD: constBreadCrumbsTitle.SETTLEMENT_GUILD,
    SETTLEMENTGUILDWITHID: constBreadCrumbsTitle.SETTLEMENT_GUILD_WITH_ID,
    IMPRESTLIST: constBreadCrumbsTitle.IMPREST_LIST,
    REPORTSUMMARY: constBreadCrumbsTitle.REPORT_SUMMERY_IMPREST,
    DELEGATIONLIST: constBreadCrumbsTitle.DEFINE_DELEGATION_LIST,
    DELEGATION: constBreadCrumbsTitle.DELEGATION_ME,
    IMPRESTDEPOSITREPORT: constBreadCrumbsTitle.REPORT_DEPOSIT_IMPREST,
    VOUCHER: constBreadCrumbsTitle.VOUCHER_LIST,
  };

  const convertToUpperCase = (str) => {
    if (!str) return '';
    return String(str).trim().toUpperCase();
  };

  const getBreadcrumbs = () => {
    const source = convertToUpperCase(useQueryData.get('source'));
    const sourceBreadcrumbs = breadcrumbEnum[source];

    if (!sourceBreadcrumbs) {
      return constBreadCrumbsTitle.REPORT_OFFLINE_TRANSACTIONS;
    }

    return [...sourceBreadcrumbs, { title: 'لیست درخواست گزارشات' }];
  };

  const clearAutoRefreshTimers = useCallback(() => {
    clearTimeout(refreshTimeoutRef.current);
  }, []);

  const scheduleAutoRefresh = useCallback(() => {
    clearAutoRefreshTimers();

    refreshTimeoutRef.current = setTimeout(() => {
      if (!requestInProgressRef.current) {
        isBackgroundRefreshRef.current = true;
        tableRef?.current?.refreshRows({
          rowKey: 'request-id',
          compareFields: REPORT_STATUS_FIELDS,
          highlightUpdatedRows: true,
        });
      }
    }, AUTO_REFRESH_DELAY * 1000);
  }, [clearAutoRefreshTimers]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      clearAutoRefreshTimers();
    };
  }, [clearAutoRefreshTimers]);

  //--API REQUESTS--//
  const getBranchesList = async (query) => {
    const isBackgroundRefresh = isBackgroundRefreshRef.current;
    isBackgroundRefreshRef.current = false;
    clearAutoRefreshTimers();
    requestInProgressRef.current = true;

    try {
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      let filter = toFilterVOS([...filters]);
      let queries = filterVOStoQueryStr(filter);
      let params = {
        offset: query ? query?.pageNumber * query?.pageSize : page * size,
        size: query ? query?.pageSize : size,
        ...queries,
      };
      if (!queries['reportKeys'] && convertToUpperCase(useQueryData.get('source'))) {
        // اگر توی فیلتر ، نام گزارش انتخاب نشده باشد ، بصورت پیش فرض براساس پارامتر ارسال شده توی آدرس بار نام گزارش ارسال میشود
        params['reportKeys'] = filterEnum[convertToUpperCase(useQueryData.get('source'))];
      }

      if (filters.find((a) => a.keyName == 'dateRange')) {
        const d = filters.find((a) => a.keyName == 'dateRange');
        d.value.start &&
          (params['requestFromDate'] = enifyDateTime(
            d?.value?.start?.format('jYYYY/jMM/jDD 00:00:00'),
          ));
        d.value.end &&
          (params['requestEndDate'] = enifyDateTime(
            d?.value?.end?.format('jYYYY/jMM/jDD 23:59:59'),
          ));
      }
      if (filters.find((a) => a.keyName == 'reportStatusKey')) {
        filters.find((a) => a.keyName == 'reportStatusKey').value &&
          (params['reportStatusKey'] = filters.find((a) => a.keyName == 'reportStatusKey').value);
      }
      if (filters.find((a) => a.keyName == 'report-name')) {
        filters.find((a) => a.keyName == 'report-name').value &&
          (params['report-name'] = filters.find((a) => a.keyName == 'report-name').value);
      }
      const data = {
        filterVOS: [],
        parentParams: [],
        orderByElementVOS: [],
      };

      setFilterParams({ data, params });

      const res = await ShareSerives.getRportOfflineRequest(params, true);
      hasPendingReportRef.current = Boolean(
        res?.result?.some?.((report) => report?.['status-key'] === 'TO_DO'),
      );
      let list = [];

      list = generateData(res?.result, {
        page: query ? query?.pageNumber : page,
        pageSize: query?.pageSize || size,
      });
      if (!isBackgroundRefresh) {
        setLoading(false);
      }

      let d = {
        data: list,
        page: query ? query?.pageNumber : page,
        totalCount: res?.count,
      };
      return d; //passed to MaterialTable
    } catch (error) {
      if (axios.isCancel(error)) {
        if (!isBackgroundRefresh) {
          setLoading(true);
        }
      } else {
        if (!isBackgroundRefresh) {
          setLoading(false);
        }
        return {
          data: [],
          page: query ? query?.pageNumber : page,
          totalCount: 0,
        };
      }
    } finally {
      requestInProgressRef.current = false;

      if (isMountedRef.current && hasPendingReportRef.current) {
        scheduleAutoRefresh();
      } else {
        clearAutoRefreshTimers();
      }
    }
  };

  //-- OTHER FUNCTIONS --//
  const onFiltersChange = (newFilters) => {
    clearAutoRefreshTimers();
    setFilters(newFilters);
    setPage(0);
    setSize(10);
    setTimeout(() => {
      tableRef?.current?.onChangeTable(true);
    }, 100);
  };

  const hint = 'در این بخش میتوانید لیست گزارشات درخواستی خود را مشاهده کنید. جهت فعال شدن دریافت فایل، صفحه را بروزرسانی نمایید. ';

  return (
    <PageContainer
      title="لیست درخواست گزارشات"
      description={hint}
      breadcrumbs={getBreadcrumbs()}
      className={classes.root}
      meta={
        <Button
          onClick={() => navigate(-1)}
          color="primary"
          endIcon={<Icons icon="CaretLeft" color="#2b3990" size="23" />}
          variant="outlined"
        >
          بازگشت
        </Button>
      }
    >
      <Grid item xs={12}>
        <Box display="flex" justifyContent="end" alignItems="flex-end" style={{ marginBottom: 16 }}>
          <FilterButton setOpenFilterDialog={setOpenFilterDialog} filters={filters} />
        </Box>
        <Box>
          <FilterPod
            filters={filters}
            onChange={onFiltersChange}
            openFilterDialog={openFilterDialog}
            setOpenFilterDialog={setOpenFilterDialog}
            handleCloseFilterDialog={handleCloseFilterDialog}
          />
        </Box>
      </Grid>
      <TableComponents
        data={getBranchesList}
        columns={columns(navigate)}
        isLoading={loading}
        ref={tableRef}
        onChange={(ps) => {
          setPage(ps?.pageNumber);
          setSize(ps?.pageSize);
          return getBranchesList(ps);
        }}
      />
    </PageContainer>
  );
}
